import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Auto-reload after a deploy.
 *
 * Uses short polling of /api/deploy/version — NOT EventSource SSE.
 * Long-lived SSE on Gunicorn workers holds a worker for the whole
 * connection and gets killed by --timeout, starving the API
 * (Not Found / Unauthorized / WORKER TIMEOUT cascades).
 */
const DEPLOY_VERSION_URL = '/api/deploy/version';
const POLL_INTERVAL_MS = 45_000;
const JITTER_MIN_MS = 1000;
const JITTER_MAX_MS = 12000;

function randomJitterMs() {
  return JITTER_MIN_MS + Math.floor(Math.random() * (JITTER_MAX_MS - JITTER_MIN_MS + 1));
}

export function useDeployRefresh() {
  const location = useLocation();
  const reloadScheduled = useRef(false);
  const knownVersion = useRef(null);

  useEffect(() => {
    if (location.pathname === '/login') return;
    if (typeof window === 'undefined') return;

    let closed = false;
    let pollTimer = null;
    let reloadTimer = null;

    const scheduleReload = (version) => {
      if (reloadScheduled.current) return;
      reloadScheduled.current = true;
      const delay = randomJitterMs();
      console.info(`[deploy-refresh] New version ${version}; reloading in ${delay}ms`);
      reloadTimer = window.setTimeout(() => window.location.reload(), delay);
    };

    const poll = async () => {
      if (closed || reloadScheduled.current) return;
      try {
        const res = await fetch(DEPLOY_VERSION_URL, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.version) return;
        if (knownVersion.current == null) {
          knownVersion.current = data.version;
          return;
        }
        if (knownVersion.current !== data.version) {
          const prev = String(knownVersion.current);
          const next = String(data.version);
          // Different Gunicorn workers used to advertise unique boot-* ids.
          // Never treat that as a deploy.
          if (prev.startsWith('boot-') && next.startsWith('boot-')) {
            return;
          }
          knownVersion.current = data.version;
          scheduleReload(data.version);
        }
      } catch {
        // ignore transient network errors
      }
    };

    void poll();
    pollTimer = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      closed = true;
      if (pollTimer) window.clearInterval(pollTimer);
      if (reloadTimer) window.clearTimeout(reloadTimer);
    };
  }, [location.pathname]);
}

export default useDeployRefresh;
